import {
  PublicDraftCreationSource,
  PublicSignupTrigger,
  PublicImageUploadAction,
  getPublicActionSessionId,
  isCurrentPublicAction,
  preparePublicSignup,
  trackPublicCta,
  trackPublicDraftCreated,
  trackPublicImageUploaded,
  trackPublicShareOpened,
} from '~publish/helpers/publicTracking'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { createPlateEditor } from 'platejs/react'
import type { RenderPlaceholderProps } from 'slate-react'

import {
  AlertDialog,
  BookTextIcon,
  Button,
  Dialog,
  LockIcon,
  Notice,
  ScanEyeIcon,
  toast,
  useMediaQuery,
  VisuallyHidden,
  WandSparklesIcon,
} from '@buffer-mono/popcorn'

import { ComposerEmojiButton } from '~publish/components/Composer/components/ComposerEmojiButton'
import { HashtagManagerButton } from '~publish/components/Composer/components/HashtagManager'
import { ComposerFrame } from '~publish/components/Composer/components/ComposerFrame'
import { ComposerToolbarFrame } from '~publish/components/Composer/components/ComposerToolbarFrame'
import { ComposerSectionsColumn } from '~publish/components/Composer/components/ComposerSectionsColumn'
import { StartThreadButtonView } from '~publish/components/Composer/components/StartThreadButtonView'
import {
  ComposerTextArea,
  TEMPLATES_NUDGE_ARIA_PLACEHOLDER,
  TEMPLATES_NUDGE_PLACEHOLDER,
  TemplatesNudgePlaceholder,
} from '~publish/components/Composer/components/ComposerTextArea'
import {
  ComposerHeaderCloseButton,
  ComposerHeaderLayout,
  ComposerHeaderMaximizeButton,
  ComposerHeaderPanelButton,
} from '~publish/components/Composer/components/Header'
import { COMPOSER_SHORTCUTS } from '~publish/constants/composerShortcuts'
import {
  GUEST_DRAFT_TEXT_MAX_LENGTH,
  isGuestDraftExpired,
} from '~publish/helpers/guestDrafts'
import type {
  GuestDraft,
  GuestDraftStore,
  SaveGuestDraftResult,
} from '~publish/helpers/guestDrafts'
import {
  getBrowserGuestDraftHandoffStore,
  getGuestAccountUrl,
  getGuestDraftHandoffRedirectPath,
} from '~publish/helpers/guestDraftHandoff'
import type {
  GuestAccountDestination,
  GuestDraftComposerHandoffAction,
  GuestDraftComposerHandoffIntent,
} from '~publish/helpers/guestDraftHandoff'
import { getBrowserGuestUnfinishedDraftStore } from '~publish/helpers/guestUnfinishedDraft'
import type { GuestMedia } from '~publish/helpers/guestMedia'
import { normalizeGuestNetworks } from '~publish/helpers/guestNetworks'
import type { GuestNetworkId } from '~publish/helpers/guestNetworks'
import type { GuestTag } from '~publish/helpers/guestTags'
import {
  GUEST_MEDIA_MAX_COUNT,
  GUEST_MEDIA_REQUIREMENTS_MESSAGE,
  getGuestMediaMaxFileSizeBytes,
  isGuestMediaMimeType,
} from '~publish/helpers/guestMedia'
import {
  getLoginUrlForRedirectPath,
  getSignupUrlForRedirectPath,
} from '~publish/helpers/publicAuthUrls'
import type { GuestMediaAccessController } from '~publish/hooks/useGuestMediaAccess'
import { useKeyboardShortcut } from '~publish/hooks/useKeyboardShortcut'
import {
  deserialize as deserializeEditor,
  serialize as serializeEditor,
} from '~publish/legacy/editor/BufferEditor'
import type {
  BufferEditor,
  BufferValue,
} from '~publish/legacy/editor/BufferEditor/types.plate'
import { QueueingTypes } from '~publish/legacy/composer/composer/AppConstants'
import { UPLOAD_IN_PROGRESS_MESSAGE } from '~publish/legacy/composer/composer/components/composer-footer/usePostValidation'
import {
  DRAFT_REQUIRES_TEXT_OR_ATTACHMENT,
  REQUIRES_TEXT_OR_ATTACHMENT,
} from '~publish/legacy/composer/composer/lib/validation/utils/validationErrorMessages'
import { ConditionalTooltip } from '~publish/legacy/composer/composer/components/ConditionalTooltip'
import { CharacterHashtagCountView } from '~publish/legacy/composer/composer/components/CharacterHashtagCountView'
import { getFormattedWhatPreventsSavingMessages } from '~publish/legacy/composer/composer/components/composer-footer/buttons/utils'
import { createCoreComposerPlugins } from '~publish/legacy/composer/composer/editor/createCoreComposerEditor'
import { DraftMethods } from '~publish/legacy/composer/composer/entities/Draft'
import { createCharacterLimitHighlightPlugin } from '~publish/legacy/editor/plugins/character-limit-highlight'
import { usePlateCharacterLimits } from '~publish/legacy/editor/plugins/character-limit-highlight/hooks'
import { getComposerConfirmationMessage } from '~publish/legacy/composer/composer/utils/GetComposerConfirmationMessage'
import { ParagraphElement } from '~publish/legacy/editor/plugins/paragraph/nodes/ParagraphElement'
import { newPost } from '~publish/legacy/routes'
import type { PublicPostTemplate } from '~publish/pages/Create/Templates/hooks/usePublicPostTemplates'

import {
  PUBLIC_TEMPLATES_PANEL_ID,
  PublicInitialTemplate,
  PublicTemplatesSidePanel,
} from './PublicTemplatesSidePanel'
import { useGuestPreviewImages } from './GuestChannelPreview'
import { PublicChannelSelector } from './PublicChannelSelector'
import {
  PublicComposerCards,
  useActiveGuestNetwork,
} from './PublicComposerCards'
import { PublicTagSelector } from './PublicTagSelector'
import { useGuestNetworkValidation } from './guestNetworkValidation'
import { PublicAccountGateDialog } from './PublicAccountGateDialog'
import { PublicGuestMediaEditor } from './PublicGuestMediaEditor'
import { PublicGuestImageWorkspace } from './PublicGuestImageWorkspace'
import type { GuestImageWorkspaceTask } from './PublicGuestImageWorkspace'
import { PublicFeatureSignupDialog } from './PublicFeatureSignupDialog'
import type { PublicFeatureSignupRequest } from './PublicFeatureSignupDialog'
import { PublicPreviewPanel } from './PublicPreviewPanel'
import { PublicPostingTimeSelector } from './PublicPostingTimeSelector'
import { PublicShareNowDialog } from './PublicShareNowDialog'
import { uploadGuestMedia } from './guestMediaClient'
import headerStyles from '~publish/components/Composer/components/Header/Header.module.css'
import styles from './PublicComposerPage.module.css'

const PREVIEW_PANEL_ID = 'public-composer-preview'
const SHARE_NOW_REQUIRES_CHANNEL = 'Please select a channel'
const SCHEDULE_IN_THE_PAST = 'The scheduled time seems to be in the past'
const MAX_TIMEOUT_DELAY = 2 ** 31 - 1
const GUEST_EDITABLE_STYLE = {
  height: '100%',
  outline: 'none',
  paddingTop: '2px',
}
type PublicComposerSidePanel = 'preview' | 'templates'
type PendingMediaUpload = Readonly<{
  file: File
  insertionIndex: number
}>
type MediaRetry =
  | Readonly<{ type: 'add'; uploads: readonly PendingMediaUpload[] }>
  | Readonly<{ type: 'replace'; mediaId: string; file: File }>
type MediaOperationError = Readonly<{
  message: string
  retry?: MediaRetry
}>

const useIsInPast = (time: number | undefined): boolean => {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (time === undefined || time <= now) return
    const timeout = setTimeout(
      () => setNow(Date.now()),
      Math.min(Math.max(time - Date.now(), 0), MAX_TIMEOUT_DELAY),
    )
    return (): void => clearTimeout(timeout)
  }, [time, now])

  return time !== undefined && time <= now
}

type MissingDraftNotice = Readonly<{
  reason: 'expired' | 'deleted'
  mediaRemoved: boolean
}>

function isSupportedGuestMediaFile(file: File): boolean {
  return (
    isGuestMediaMimeType(file.type) &&
    file.size > 0 &&
    file.size <= getGuestMediaMaxFileSizeBytes(file.type) &&
    file.name.trim().length > 0
  )
}

function insertUploadedMedia(
  currentMedia: readonly GuestMedia[],
  uploads: readonly Readonly<{
    media: GuestMedia
    insertionIndex: number
  }>[],
): readonly GuestMedia[] {
  const nextMedia = [...currentMedia]

  for (const upload of uploads.toSorted(
    (left, right) => left.insertionIndex - right.insertionIndex,
  )) {
    nextMedia.splice(
      Math.min(upload.insertionIndex, nextMedia.length),
      0,
      upload.media,
    )
  }

  return nextMedia
}

function continueWithAccountHandoff(
  intent: GuestDraftComposerHandoffIntent,
  destination: GuestAccountDestination,
): void {
  const redirectPath = getGuestDraftHandoffRedirectPath(intent.id)
  const authUrl =
    destination === 'login'
      ? getLoginUrlForRedirectPath(redirectPath)
      : getSignupUrlForRedirectPath(redirectPath)

  window.location.assign(authUrl)
}

export function PublicComposerPage({
  store,
  initialDraft,
  mediaAccess,
  onDraftIdChange,
  onDismiss,
  onDraftSaved,
  onDraftRemoved,
  onLimitReached,
}: {
  store: GuestDraftStore
  initialDraft?: GuestDraft
  mediaAccess: GuestMediaAccessController
  onDraftIdChange: (id: string | undefined) => void
  onDismiss: () => void
  onDraftSaved: (id: string) => void
  onDraftRemoved: () => void
  onLimitReached: () => void
}): JSX.Element {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const scheduledAtValue = Number(searchParams.get('scheduledAt'))
  const [scheduledAt, setScheduledAt] = useState(() => {
    if (initialDraft) return initialDraft.scheduledAt
    return Number.isSafeInteger(scheduledAtValue) &&
      scheduledAtValue > 0 &&
      Number.isFinite(new Date(scheduledAtValue).getTime())
      ? scheduledAtValue
      : undefined
  })
  const isScheduledInPast = useIsInPast(scheduledAt)
  const initialTemplateId = searchParams.get('templateId')
  const [editor] = useState<BufferEditor>(() => {
    const nextEditor = createPlateEditor<BufferValue>({
      id: 'public-composer',
      plugins: createCoreComposerPlugins({
        afterEmoji: [createCharacterLimitHighlightPlugin()],
      }),
    }) as unknown as BufferEditor
    nextEditor.children = ParagraphElement.paragraphsFromText(
      initialDraft?.text ?? '',
    )
    return nextEditor
  })
  const [text, setText] = useState(initialDraft?.text ?? '')
  const [media, setMedia] = useState<readonly GuestMedia[]>(
    initialDraft?.media ?? [],
  )
  const [tags, setTags] = useState<readonly GuestTag[]>(
    initialDraft?.tags ?? [],
  )
  const [initialNetworks] = useState<readonly GuestNetworkId[] | undefined>(
    () =>
      initialDraft
        ? initialDraft.networks
        : searchParams.has('network')
          ? normalizeGuestNetworks([searchParams.get('network')])
          : undefined,
  )
  const [networks, setNetworks] = useState<readonly GuestNetworkId[]>(
    initialNetworks ?? [],
  )
  const isSidePanelOverlay = useMediaQuery('(width < 900px)')
  const [activeSidePanel, setActiveSidePanel] =
    useState<PublicComposerSidePanel | null>(() =>
      isSidePanelOverlay ? null : initialTemplateId ? 'templates' : 'preview',
    )
  const [isMaximized, setIsMaximized] = useState(false)
  const [hasSaveError, setHasSaveError] = useState(false)
  const [hasSaveConflict, setHasSaveConflict] = useState(false)
  const [hasTextLimitError, setHasTextLimitError] = useState(false)
  const [isUnsaved, setIsUnsaved] = useState(false)
  const [missingDraftNotice, setMissingDraftNotice] =
    useState<MissingDraftNotice>()
  const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] = useState(false)
  const [isShareNowOpen, setIsShareNowOpen] = useState(false)
  const [accountHandoffIntent, setAccountHandoffIntent] =
    useState<GuestDraftComposerHandoffIntent>()
  const [hasAccountHandoffError, setHasAccountHandoffError] = useState(false)
  const [pendingTemplate, setPendingTemplate] =
    useState<PublicPostTemplate | null>(null)
  const [pendingUploadCount, setPendingUploadCount] = useState(0)
  const [pendingUploadProgress, setPendingUploadProgress] = useState(0)
  const [replacingMediaIds, setReplacingMediaIds] = useState<
    ReadonlySet<string>
  >(() => new Set())
  const [mediaOperationError, setMediaOperationError] =
    useState<MediaOperationError>()
  const [mediaToEdit, setMediaToEdit] = useState<{
    media: GuestMedia
    task: GuestImageWorkspaceTask
  }>()
  const [featureSignupRequest, setFeatureSignupRequest] =
    useState<PublicFeatureSignupRequest>()
  const appliedTemplateIdRef = useRef<string>()
  const draftIdRef = useRef(initialDraft?.id)
  const draftUpdatedAtRef = useRef(initialDraft?.updatedAt)
  const draftCreatedAtRef = useRef(initialDraft?.createdAt)
  const isPersistingRef = useRef(false)
  const textRef = useRef(text)
  const mediaRef = useRef(media)
  const tagsRef = useRef<readonly GuestTag[] | undefined>(initialDraft?.tags)
  const networksRef = useRef<readonly GuestNetworkId[] | undefined>(
    initialNetworks,
  )
  const scheduledAtRef = useRef(scheduledAt)
  const isMountedRef = useRef(true)
  const mediaMutationInProgressRef = useRef(false)
  const mediaOperationGenerationRef = useRef(0)
  const mediaUploadControllerRef = useRef<AbortController>()
  const imageEditTriggerRef = useRef<HTMLButtonElement>()
  const templateTriggerRef = useRef<HTMLElement | null>(null)
  const isMediaBusy = pendingUploadCount > 0 || replacingMediaIds.size > 0
  const isSmallViewport = useMediaQuery('(width <= 880px)')
  const isTemplatesPanelOpen = activeSidePanel === 'templates'
  const editableProps = useMemo(
    () => ({
      spellCheck: true,
      placeholder: TEMPLATES_NUDGE_PLACEHOLDER,
      'aria-placeholder': TEMPLATES_NUDGE_ARIA_PLACEHOLDER,
      renderPlaceholder: (props: RenderPlaceholderProps): JSX.Element => (
        <TemplatesNudgePlaceholder
          {...props}
          templatesButtonLabel="Browse templates"
          templatesPanelId={PUBLIC_TEMPLATES_PANEL_ID}
          templatesPanelOpen={isTemplatesPanelOpen}
          onOpenTemplates={(): void => setActiveSidePanel('templates')}
        />
      ),
      autoFocus: true,
      'aria-label': 'Post text',
      style: GUEST_EDITABLE_STYLE,
    }),
    [isTemplatesPanelOpen],
  )

  const persistContent = useCallback(
    (
      nextText: string,
      nextMedia: readonly GuestMedia[],
      trackCreation = true,
    ): boolean => {
      const saveContent = (
        contentMedia: readonly GuestMedia[],
      ): SaveGuestDraftResult =>
        store.save(
          draftIdRef.current,
          {
            text: nextText,
            media: contentMedia,
            tags: tagsRef.current,
            networks: networksRef.current,
            scheduledAt: scheduledAtRef.current ?? null,
          },
          draftUpdatedAtRef.current,
        )

      isPersistingRef.current = true
      let result: SaveGuestDraftResult
      try {
        result = saveContent(nextMedia)
        if (result.status === 'expired') {
          const hadDraft = draftIdRef.current !== undefined
          const createdAt = draftCreatedAtRef.current
          const reason =
            createdAt !== undefined &&
            !isGuestDraftExpired({ createdAt }, Date.now())
              ? 'deleted'
              : 'expired'
          draftIdRef.current = undefined
          draftUpdatedAtRef.current = undefined
          draftCreatedAtRef.current = undefined
          if (hadDraft) onDraftIdChange(undefined)
          setMissingDraftNotice({ reason, mediaRemoved: false })
          result = saveContent(nextMedia)
          if (result.status === 'expired' && nextMedia.length > 0) {
            mediaRef.current = []
            setMedia([])
            setMissingDraftNotice({ reason, mediaRemoved: true })
            result = saveContent([])
          }
        }
      } finally {
        isPersistingRef.current = false
      }

      if (result.status === 'too-long') {
        setHasSaveError(false)
        setHasSaveConflict(false)
        setHasTextLimitError(true)
        setIsUnsaved(true)
        return false
      }

      setHasTextLimitError(false)

      if (result.status === 'saved') {
        const previousDraftId = draftIdRef.current
        draftIdRef.current = result.draft.id
        draftUpdatedAtRef.current = result.draft.updatedAt
        draftCreatedAtRef.current = result.draft.createdAt
        setHasSaveError(false)
        setHasSaveConflict(false)
        setIsUnsaved(false)
        if (previousDraftId !== result.draft.id) {
          if (trackCreation)
            trackPublicDraftCreated({
              creationSource: appliedTemplateIdRef.current
                ? PublicDraftCreationSource.TEMPLATE
                : PublicDraftCreationSource.BLANK,
              templateId: appliedTemplateIdRef.current,
              hasMedia: result.draft.media.length > 0,
              draftCount: store.getSnapshot().drafts.length,
            })
          if (previousDraftId === undefined) {
            getBrowserGuestUnfinishedDraftStore().mark(result.draft.id)
          }
          onDraftIdChange(result.draft.id)
        }
        return true
      }

      if (result.status === 'unchanged') {
        draftUpdatedAtRef.current = result.draft.updatedAt
        setHasSaveError(false)
        setHasSaveConflict(false)
        setIsUnsaved(false)
        return true
      }

      if (result.status === 'empty' || result.status === 'deleted') {
        const removedDraftId = draftIdRef.current
        const hadDraft = removedDraftId !== undefined
        if (removedDraftId) {
          getBrowserGuestUnfinishedDraftStore().clear(removedDraftId)
        }
        draftIdRef.current = undefined
        draftUpdatedAtRef.current = undefined
        draftCreatedAtRef.current = undefined
        setHasSaveError(false)
        setHasSaveConflict(false)
        setIsUnsaved(false)
        if (hadDraft) onDraftIdChange(undefined)
        return false
      }

      setIsUnsaved(true)
      if (result.status === 'conflict') {
        setHasSaveError(false)
        setHasSaveConflict(true)
        return false
      }

      if (result.status === 'limit-reached') {
        setHasSaveError(false)
        setHasSaveConflict(false)
        onLimitReached()
        return false
      }

      setHasSaveConflict(false)
      setHasSaveError(true)
      return false
    },
    [onDraftIdChange, onLimitReached, store],
  )

  const saveAsNewDraft = (): void => {
    draftIdRef.current = undefined
    draftUpdatedAtRef.current = undefined
    draftCreatedAtRef.current = undefined
    setHasSaveConflict(false)
    persistContent(textRef.current, mediaRef.current)
  }

  const persistValue = useCallback(
    (_value: BufferValue): void => {
      const nextText = serializeEditor(editor).text
      setText(nextText)
      textRef.current = nextText
      persistContent(nextText, mediaRef.current)
    },
    [editor, persistContent],
  )

  const applyTemplate = useCallback(
    (template: PublicPostTemplate): void => {
      deserializeEditor(editor, template.body)
      setText(template.body)
      textRef.current = template.body
      appliedTemplateIdRef.current = template.id
      persistContent(template.body, mediaRef.current)
      setPendingTemplate(null)
      if (isSidePanelOverlay) {
        setActiveSidePanel(null)
        editor.tf.focus()
      }
    },
    [editor, isSidePanelOverlay, persistContent],
  )

  const persistMedia = useCallback(
    (nextMedia: readonly GuestMedia[], trackCreation = true): void => {
      mediaRef.current = nextMedia
      setMedia(nextMedia)
      persistContent(textRef.current, nextMedia, trackCreation)
    },
    [persistContent],
  )

  const addMediaFiles = useCallback(
    async (
      selectedFiles: readonly File[],
      requestedInsertionIndexes?: readonly number[],
    ): Promise<void> => {
      if (mediaMutationInProgressRef.current || selectedFiles.length === 0) {
        return
      }

      const currentMedia = mediaRef.current
      const remainingSlots = GUEST_MEDIA_MAX_COUNT - currentMedia.length
      if (remainingSlots <= 0) {
        setMediaOperationError({
          message: `A draft can contain up to ${GUEST_MEDIA_MAX_COUNT} images.`,
        })
        return
      }

      const selectedUploads = selectedFiles.map((file, index) => ({
        file,
        insertionIndex:
          requestedInsertionIndexes?.[index] ?? currentMedia.length + index,
      }))
      const supportedUploads = selectedUploads.filter(({ file }) =>
        isSupportedGuestMediaFile(file),
      )
      const invalidFileCount = selectedUploads.length - supportedUploads.length
      const uploads = supportedUploads.slice(0, remainingSlots)
      const excessFileCount = supportedUploads.length - uploads.length

      if (uploads.length === 0) {
        setMediaOperationError({
          message:
            invalidFileCount > 0
              ? GUEST_MEDIA_REQUIREMENTS_MESSAGE
              : `A draft can contain up to ${GUEST_MEDIA_MAX_COUNT} images.`,
        })
        return
      }

      mediaMutationInProgressRef.current = true
      const trackingSessionId = getPublicActionSessionId()
      const operationGeneration = ++mediaOperationGenerationRef.current
      const controller = new AbortController()
      mediaUploadControllerRef.current = controller
      setPendingUploadCount(uploads.length)
      setPendingUploadProgress(0)
      setMediaOperationError(undefined)

      const totalBytes = uploads.reduce((sum, { file }) => sum + file.size, 0)
      const uploadedBytes = uploads.map(() => 0)
      const reportProgress = (index: number, fraction: number): void => {
        if (
          !isMountedRef.current ||
          operationGeneration !== mediaOperationGenerationRef.current
        ) {
          return
        }
        uploadedBytes[index] = fraction * (uploads[index]?.file.size ?? 0)
        const loaded = uploadedBytes.reduce((sum, bytes) => sum + bytes, 0)
        setPendingUploadProgress(
          totalBytes > 0 ? Math.min(100, (loaded / totalBytes) * 100) : 0,
        )
      }

      try {
        const currentMediaIds = currentMedia.map((asset) => asset.id)
        const results = await Promise.allSettled(
          uploads.map(({ file }, index) =>
            uploadGuestMedia({
              file,
              currentMediaIds,
              signal: controller.signal,
              onProgress: (fraction): void => reportProgress(index, fraction),
            }),
          ),
        )
        if (
          !isMountedRef.current ||
          operationGeneration !== mediaOperationGenerationRef.current
        ) {
          return
        }

        const uploadedMedia = results.flatMap((result, index) => {
          const upload = uploads[index]
          return result.status === 'fulfilled' && upload
            ? [
                {
                  media: result.value,
                  insertionIndex: upload.insertionIndex,
                },
              ]
            : []
        })
        const failedUploads = uploads.filter(
          (_upload, index) => results[index]?.status === 'rejected',
        )
        if (uploadedMedia.length > 0) {
          persistMedia(
            insertUploadedMedia(currentMedia, uploadedMedia),
            isCurrentPublicAction(trackingSessionId),
          )
          trackPublicImageUploaded(
            trackingSessionId,
            PublicImageUploadAction.ADD,
            uploadedMedia.length,
          )
        }

        const issueMessages = [
          failedUploads.length > 0
            ? `${failedUploads.length} ${failedUploads.length === 1 ? 'image' : 'images'} could not be uploaded.`
            : null,
          invalidFileCount > 0
            ? `${invalidFileCount} ${invalidFileCount === 1 ? 'file was' : 'files were'} not a supported image.`
            : null,
          excessFileCount > 0
            ? `${excessFileCount} ${excessFileCount === 1 ? 'image was' : 'images were'} over the 10-image limit.`
            : null,
        ].filter((message): message is string => message !== null)

        if (issueMessages.length > 0) {
          setMediaOperationError({
            message: issueMessages.join(' '),
            ...(failedUploads.length > 0
              ? { retry: { type: 'add', uploads: failedUploads } }
              : {}),
          })
        }
      } finally {
        if (mediaUploadControllerRef.current === controller) {
          mediaUploadControllerRef.current = undefined
        }
        if (
          isMountedRef.current &&
          operationGeneration === mediaOperationGenerationRef.current
        ) {
          mediaMutationInProgressRef.current = false
          setPendingUploadCount(0)
        }
      }
    },
    [persistMedia],
  )

  const replaceMediaFile = useCallback(
    async (mediaId: string, selectedFiles: readonly File[]): Promise<void> => {
      const file = selectedFiles[0]
      if (!file || mediaMutationInProgressRef.current) return

      if (!isSupportedGuestMediaFile(file)) {
        setMediaOperationError({
          message: GUEST_MEDIA_REQUIREMENTS_MESSAGE,
        })
        return
      }

      const currentMedia = mediaRef.current
      if (!currentMedia.some((asset) => asset.id === mediaId)) return

      mediaMutationInProgressRef.current = true
      const trackingSessionId = getPublicActionSessionId()
      const operationGeneration = ++mediaOperationGenerationRef.current
      const controller = new AbortController()
      mediaUploadControllerRef.current = controller
      setMediaOperationError(undefined)
      setReplacingMediaIds(new Set([mediaId]))

      try {
        const replacement = await uploadGuestMedia({
          file,
          currentMediaIds: currentMedia.map((asset) => asset.id),
          mediaId,
          signal: controller.signal,
        })
        if (
          !isMountedRef.current ||
          operationGeneration !== mediaOperationGenerationRef.current
        ) {
          return
        }

        persistMedia(
          currentMedia.map((asset) =>
            asset.id === mediaId ? replacement : asset,
          ),
          isCurrentPublicAction(trackingSessionId),
        )
        trackPublicImageUploaded(
          trackingSessionId,
          PublicImageUploadAction.REPLACE,
          1,
        )
      } catch {
        if (
          isMountedRef.current &&
          operationGeneration === mediaOperationGenerationRef.current
        ) {
          setMediaOperationError({
            message: 'The replacement image could not be uploaded.',
            retry: { type: 'replace', mediaId, file },
          })
        }
      } finally {
        if (mediaUploadControllerRef.current === controller) {
          mediaUploadControllerRef.current = undefined
        }
        if (
          isMountedRef.current &&
          operationGeneration === mediaOperationGenerationRef.current
        ) {
          mediaMutationInProgressRef.current = false
          setReplacingMediaIds(new Set())
        }
      }
    },
    [persistMedia],
  )

  const removeMedia = useCallback(
    (mediaId: string): void => {
      if (mediaMutationInProgressRef.current) return

      setMediaOperationError(undefined)
      persistMedia(mediaRef.current.filter((asset) => asset.id !== mediaId))
    },
    [persistMedia],
  )

  const retryMediaOperation = useCallback((): void => {
    const retry = mediaOperationError?.retry
    if (!retry) return

    if (retry.type === 'add') {
      void addMediaFiles(
        retry.uploads.map(({ file }) => file),
        retry.uploads.map(({ insertionIndex }) => insertionIndex),
      )
      return
    }

    void replaceMediaFile(retry.mediaId, [retry.file])
  }, [addMediaFiles, mediaOperationError?.retry, replaceMediaFile])

  const requestTemplateUse = useCallback(
    (template: PublicPostTemplate): void => {
      if (text.trim().length > 0 && text !== template.body) {
        templateTriggerRef.current =
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null
        setPendingTemplate(template)
        return
      }

      applyTemplate(template)
    },
    [applyTemplate, text],
  )

  const clearInitialTemplate = useCallback((): void => {
    setSearchParams(
      (currentSearchParams) => {
        const nextSearchParams = new URLSearchParams(currentSearchParams)
        nextSearchParams.delete('templateId')
        return nextSearchParams
      },
      { replace: true, state: location.state },
    )
  }, [location.state, setSearchParams])

  const selectPostingTime = (nextScheduledAt: number | undefined): void => {
    scheduledAtRef.current = nextScheduledAt
    setScheduledAt(nextScheduledAt)
    setSearchParams(
      (currentSearchParams) => {
        const nextSearchParams = new URLSearchParams(currentSearchParams)
        if (nextScheduledAt === undefined) {
          nextSearchParams.delete('scheduledAt')
        } else {
          nextSearchParams.set('scheduledAt', nextScheduledAt.toString())
        }
        return nextSearchParams
      },
      { replace: true, state: location.state },
    )
    if (textRef.current.trim().length > 0 || mediaRef.current.length > 0) {
      persistContent(textRef.current, mediaRef.current)
    }
  }

  const toggleSidePanel = useCallback(
    (sidePanel: PublicComposerSidePanel): void => {
      setActiveSidePanel((currentSidePanel) =>
        currentSidePanel === sidePanel ? null : sidePanel,
      )
    },
    [],
  )

  const toggleMaximize = useCallback(
    () => setIsMaximized((maximized) => !maximized),
    [],
  )

  const leaveComposer = useCallback((): void => {
    mediaOperationGenerationRef.current += 1
    mediaUploadControllerRef.current?.abort()
    mediaUploadControllerRef.current = undefined
    mediaMutationInProgressRef.current = false
    setPendingUploadCount(0)
    setReplacingMediaIds(new Set())
    onDismiss()
  }, [onDismiss])

  useKeyboardShortcut('mod+shift+f', toggleMaximize)

  useEffect(() => {
    isMountedRef.current = true

    return (): void => {
      isMountedRef.current = false
      mediaOperationGenerationRef.current += 1
      mediaUploadControllerRef.current?.abort()
      mediaUploadControllerRef.current = undefined
      mediaMutationInProgressRef.current = false
    }
  }, [])

  useEffect(() => {
    if (initialTemplateId && !isSidePanelOverlay) {
      setActiveSidePanel('templates')
    }
  }, [initialTemplateId, isSidePanelOverlay])

  const handleDraftStoreChangeRef = useRef<() => void>()
  handleDraftStoreChangeRef.current = (): void => {
    const draftId = draftIdRef.current
    if (!draftId || isPersistingRef.current || store.getById(draftId)) return

    const createdAt = draftCreatedAtRef.current
    if (
      createdAt !== undefined &&
      isGuestDraftExpired({ createdAt }, Date.now())
    ) {
      persistContent(textRef.current, mediaRef.current)
      return
    }

    onDraftRemoved()
  }

  useEffect(
    () =>
      store.subscribe(() => {
        handleDraftStoreChangeRef.current?.()
      }),
    [store],
  )

  useEffect(() => {
    if (!isUnsaved && !isMediaBusy) return

    const warnBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', warnBeforeUnload)
    return (): void =>
      window.removeEventListener('beforeunload', warnBeforeUnload)
  }, [isMediaBusy, isUnsaved])

  const requestDismiss = (): void => {
    if (isUnsaved || isMediaBusy) {
      setIsCloseConfirmationOpen(true)
      return
    }

    leaveComposer()
  }

  const openShareNow = (): void => {
    if (isMediaBusy || networks.length === 0) return

    const isSaved = persistContent(textRef.current, mediaRef.current)
    if (isSaved) {
      trackPublicShareOpened(textRef.current, mediaRef.current.length > 0)
      setIsShareNowOpen(true)
    }
  }

  const createAccountHandoff = (
    action: GuestDraftComposerHandoffAction,
  ): GuestDraftComposerHandoffIntent | undefined => {
    if (isMediaBusy) return

    const isSaved = persistContent(textRef.current, mediaRef.current)
    const initiatingDraftId = draftIdRef.current
    if (!isSaved || !initiatingDraftId) return

    const result = getBrowserGuestDraftHandoffStore().create({
      action,
      initiatingDraftId,
      scheduledAt:
        action === 'schedule' && scheduledAt
          ? Math.floor(scheduledAt / 1000)
          : undefined,
    })
    if (result.status === 'write-failed') {
      setHasAccountHandoffError(true)
      return
    }

    setHasAccountHandoffError(false)
    return result.intent
  }

  const openAccountGate = (action: GuestDraftComposerHandoffAction): void => {
    const intent = createAccountHandoff(action)
    if (intent) {
      trackPublicCta(
        `publish-composer-footer-${action}Signup-1`,
        action === 'schedule'
          ? PublicSignupTrigger.SCHEDULE
          : PublicSignupTrigger.SAVE_DRAFT,
      )
      setAccountHandoffIntent(intent)
    }
  }

  const closeAccountGate = (): void => {
    if (!accountHandoffIntent) return

    getBrowserGuestDraftHandoffStore().clear(accountHandoffIntent.id)
    setAccountHandoffIntent(undefined)
  }

  const continueFromSignupPrompt = (
    destination: GuestAccountDestination,
  ): void => {
    const hasCurrentContent =
      textRef.current.trim().length > 0 || mediaRef.current.length > 0
    if (hasCurrentContent && !isMediaBusy) {
      const intent = createAccountHandoff('saveDraft')
      if (intent) {
        continueWithAccountHandoff(intent, destination)
        return
      }
      setFeatureSignupRequest(undefined)
      return
    }

    const url = getGuestAccountUrl({
      destination,
      hasGuestDrafts: store.getSnapshot().drafts.length > 0,
      fallbackPath: newPost.route,
    })
    if (url) {
      window.location.assign(url)
      return
    }
    setHasAccountHandoffError(true)
    setFeatureSignupRequest(undefined)
  }

  const continueToAccount = (destination: GuestAccountDestination): void => {
    if (accountHandoffIntent) {
      trackPublicCta(
        `publish-composer-accountGate-${destination === 'signup' ? `${accountHandoffIntent.action}Signup` : 'logIn'}-1`,
      )
      preparePublicSignup(
        accountHandoffIntent.action === 'schedule'
          ? PublicSignupTrigger.SCHEDULE
          : PublicSignupTrigger.SAVE_DRAFT,
      )
      continueWithAccountHandoff(accountHandoffIntent, destination)
    }
  }

  const createAccountAfterShare = (): boolean => {
    const intent = createAccountHandoff('saveDraft')
    if (!intent) return false

    trackPublicCta(
      'publish-composer-shareNowPanel-signupPrompt-1',
      PublicSignupTrigger.SHARE_NOW_PROMPT,
    )
    continueWithAccountHandoff(intent, 'signup')
    return true
  }

  const [activeNetwork, setActiveNetwork] = useActiveGuestNetwork(networks)
  const canTagPeople = networks.includes('instagram')
  const { images: previewImages } = useGuestPreviewImages(media, mediaAccess)

  const requestChannelSettingsSignup = (trigger: HTMLElement): void => {
    setFeatureSignupRequest({ feature: 'channel-settings', trigger })
  }

  const hasContent = text.trim().length > 0 || media.length > 0
  const accountActionDisabled =
    isMediaBusy || !hasContent || !!featureSignupRequest
  const getDisabledReason = (emptyMessage: string): string | null => {
    if (isMediaBusy) return UPLOAD_IN_PROGRESS_MESSAGE
    if (!hasContent) return emptyMessage
    return null
  }

  const validation = useGuestNetworkValidation({
    networks,
    editor,
    text,
    media,
  })
  const activeDraft = activeNetwork
    ? validation.drafts.get(activeNetwork)
    : undefined
  const [isDirty, setIsDirty] = useState(hasContent)
  useEffect(() => {
    if (hasContent) setIsDirty(true)
  }, [hasContent])
  const getTextCharacterCount = useCallback(
    (value: string): number =>
      activeDraft
        ? DraftMethods.getTextCharacterCount(activeDraft, value)
        : value.length,
    [activeDraft],
  )
  const getAdditionalCharacterCount = useCallback(
    (value: string): number =>
      activeDraft
        ? DraftMethods.getAdditionalCharacterCount(activeDraft, value)
        : 0,
    [activeDraft],
  )
  const decorate = usePlateCharacterLimits(
    editor,
    activeDraft ? DraftMethods.getCharLimit(activeDraft) : null,
    getTextCharacterCount,
    getAdditionalCharacterCount,
  )
  const editablePropsWithLimits = useMemo(
    () => ({ ...editableProps, decorate }),
    [editableProps, decorate],
  )
  const postValidationIssues = getFormattedWhatPreventsSavingMessages({
    warningMessages: [...validation.whatPreventsSaving],
  })
  const channelIssue =
    !scheduledAt && networks.length === 0 ? SHARE_NOW_REQUIRES_CHANNEL : null
  const scheduleIssue = isScheduledInPast ? SCHEDULE_IN_THE_PAST : null
  const postActionIssues = postValidationIssues ?? scheduleIssue ?? channelIssue
  const postActionDisabled = accountActionDisabled || postActionIssues !== null
  const draftDisabledReason = getDisabledReason(
    DRAFT_REQUIRES_TEXT_OR_ATTACHMENT,
  )
  const postActionReason =
    channelIssue ??
    getDisabledReason(REQUIRES_TEXT_OR_ATTACHMENT) ??
    postValidationIssues ??
    scheduleIssue
  const draftDisabledReasonId = useId()
  const postActionReasonId = useId()

  const saveAsDraft = (): void => {
    if (accountActionDisabled || mediaMutationInProgressRef.current) return
    if (!persistContent(textRef.current, mediaRef.current)) return

    if (draftIdRef.current) {
      getBrowserGuestUnfinishedDraftStore().clear(draftIdRef.current)
      onDraftSaved(draftIdRef.current)
    }

    toast.success(
      getComposerConfirmationMessage({
        entityName: 'post',
        draftMode: true,
        queueingType: QueueingTypes.ADD_DRAFT,
      }),
    )
    leaveComposer()
  }

  useKeyboardShortcut(COMPOSER_SHORTCUTS.SAVE_DRAFT, saveAsDraft, {
    disabled:
      accountActionDisabled ||
      isCloseConfirmationOpen ||
      isShareNowOpen ||
      !!accountHandoffIntent ||
      !!pendingTemplate ||
      !!mediaToEdit,
    ignoreInputFocus: false,
  })

  return (
    <>
      <Dialog
        open
        onOpenChange={(open): void => {
          if (!open) requestDismiss()
        }}
      >
        <ComposerFrame.Content
          state={isSmallViewport || isMaximized ? 'maximized' : 'default'}
        >
          <ComposerHeaderLayout
            sidePanelOpen={activeSidePanel !== null}
            title={
              <>
                <Dialog.Title>Create Post</Dialog.Title>
                <VisuallyHidden>
                  <Dialog.Description>
                    Write a post and review its preview.
                  </Dialog.Description>
                </VisuallyHidden>
              </>
            }
            tags={
              <PublicTagSelector
                store={store}
                selectedTags={tags}
                onChange={(nextTags): void => {
                  tagsRef.current = nextTags
                  setTags(nextTags)
                  persistContent(textRef.current, mediaRef.current)
                }}
              />
            }
            actions={
              <>
                <ComposerHeaderPanelButton
                  label="Templates"
                  active={activeSidePanel === 'templates'}
                  controls={PUBLIC_TEMPLATES_PANEL_ID}
                  onClick={(): void => toggleSidePanel('templates')}
                >
                  <BookTextIcon />
                </ComposerHeaderPanelButton>
                <ComposerHeaderPanelButton
                  label="AI Assistant"
                  active={featureSignupRequest?.feature === 'ai-assistant'}
                  onClick={(event): void => {
                    setFeatureSignupRequest({
                      feature: 'ai-assistant',
                      trigger: event.currentTarget,
                    })
                  }}
                >
                  <WandSparklesIcon />
                </ComposerHeaderPanelButton>
                <ComposerHeaderPanelButton
                  label="Preview"
                  active={activeSidePanel === 'preview'}
                  controls={PREVIEW_PANEL_ID}
                  onClick={(): void => toggleSidePanel('preview')}
                >
                  <ScanEyeIcon />
                </ComposerHeaderPanelButton>
                <ComposerHeaderMaximizeButton
                  isMaximized={isMaximized}
                  onToggle={toggleMaximize}
                />
                <ComposerHeaderCloseButton
                  className={headerStyles.closeButton}
                  onClick={requestDismiss}
                />
              </>
            }
          />
          <Dialog.Separator />
          <ComposerFrame.Body>
            <ComposerSectionsColumn sidePanelOpen={activeSidePanel !== null}>
              <PublicChannelSelector
                selectedNetworks={networks}
                onChange={(nextNetworks): void => {
                  networksRef.current = nextNetworks
                  setNetworks(nextNetworks)
                  persistContent(textRef.current, mediaRef.current)
                }}
              />
              <PublicComposerCards
                validation={validation}
                isDirty={isDirty}
                notices={
                  <div className={styles.notices}>
                    {hasSaveConflict ? (
                      <Notice variant="warning">
                        <Notice.Heading>
                          Draft changed in another tab
                        </Notice.Heading>
                        <Notice.Text>
                          Your changes are still here. Save them as a new draft
                          to keep both versions.
                        </Notice.Text>
                        <Notice.Actions>
                          <Button size="small" onClick={saveAsNewDraft}>
                            Save as New Draft
                          </Button>
                        </Notice.Actions>
                      </Notice>
                    ) : hasTextLimitError ? (
                      <Notice variant="error">
                        <Notice.Heading>Draft is too long</Notice.Heading>
                        <Notice.Text>
                          A draft can contain up to{' '}
                          {GUEST_DRAFT_TEXT_MAX_LENGTH.toLocaleString()}{' '}
                          characters. Shorten this draft to save it.
                        </Notice.Text>
                      </Notice>
                    ) : hasSaveError ? (
                      <Notice variant="error">
                        <Notice.Heading>Draft not saved</Notice.Heading>
                        <Notice.Text>
                          We couldn’t save this draft in your browser. Your
                          changes are still here.
                        </Notice.Text>
                        <Notice.Actions>
                          <Button
                            size="small"
                            onClick={(): void => {
                              persistContent(textRef.current, mediaRef.current)
                            }}
                          >
                            Try Again
                          </Button>
                        </Notice.Actions>
                      </Notice>
                    ) : null}
                    {missingDraftNotice ? (
                      <Notice variant="warning">
                        <Notice.Heading>
                          {missingDraftNotice.reason === 'deleted'
                            ? 'Draft deleted'
                            : 'Draft expired'}
                        </Notice.Heading>
                        <Notice.Text>
                          {missingDraftNotice.reason === 'deleted'
                            ? 'This draft was deleted in another tab.'
                            : 'Drafts stay in this browser for up to 30 days.'}{' '}
                          Your text is still here, and we saved it as a new
                          draft.
                          {missingDraftNotice.mediaRemoved
                            ? ' Its images were too old to keep, so they were removed.'
                            : null}
                        </Notice.Text>
                      </Notice>
                    ) : null}
                    {hasAccountHandoffError ? (
                      <Notice variant="error">
                        <Notice.Heading>
                          Account transfer not ready
                        </Notice.Heading>
                        <Notice.Text>
                          We couldn’t prepare these drafts for transfer. They
                          remain in this browser. Try the account action again.
                        </Notice.Text>
                      </Notice>
                    ) : null}
                    {mediaOperationError ? (
                      <Notice variant="error">
                        <Notice.Heading>Image upload issue</Notice.Heading>
                        <Notice.Text>{mediaOperationError.message}</Notice.Text>
                        {mediaOperationError.retry ? (
                          <Notice.Actions>
                            <Button size="small" onClick={retryMediaOperation}>
                              Try Again
                            </Button>
                          </Notice.Actions>
                        ) : null}
                      </Notice>
                    ) : null}
                  </div>
                }
                networks={networks}
                activeNetwork={activeNetwork}
                onActivateNetwork={setActiveNetwork}
                text={text}
                images={previewImages}
                uploadDisabled={
                  isMediaBusy || media.length >= GUEST_MEDIA_MAX_COUNT
                }
                onUploadFiles={(files): void => {
                  void addMediaFiles(files)
                }}
                onRequestSignIn={requestChannelSettingsSignup}
                editor={
                  <>
                    <ComposerTextArea
                      editor={editor}
                      editableProps={editablePropsWithLimits}
                      onChange={persistValue}
                      expanded
                      hasLinkAttachment={false}
                      imageFirst={false}
                    />
                    <PublicGuestMediaEditor
                      media={media}
                      access={mediaAccess}
                      pendingUploadCount={pendingUploadCount}
                      pendingUploadProgress={pendingUploadProgress}
                      replacingMediaIds={replacingMediaIds}
                      editingMediaId={mediaToEdit?.media.id}
                      disabled={isMediaBusy}
                      canTagPeople={canTagPeople}
                      onAddFiles={(files): void => {
                        void addMediaFiles(files)
                      }}
                      onReplace={(mediaId, files): void => {
                        void replaceMediaFile(mediaId, files)
                      }}
                      onEdit={(selectedMedia, trigger, task): void => {
                        imageEditTriggerRef.current = trigger
                        setMediaToEdit({ media: selectedMedia, task })
                      }}
                      onRequestTaggingSignup={(trigger): void => {
                        setFeatureSignupRequest({ feature: 'tagging', trigger })
                      }}
                      onRemove={(mediaId): void => {
                        removeMedia(mediaId)
                        editor.tf.focus()
                      }}
                    />
                  </>
                }
                toolbar={
                  <ComposerToolbarFrame>
                    <div className={styles.toolbar}>
                      <div className={styles.toolbarStart}>
                        <ComposerEmojiButton editor={editor} />
                        <HashtagManagerButton
                          onClick={(event): void =>
                            setFeatureSignupRequest({
                              feature: 'hashtag-manager',
                              trigger: event.currentTarget,
                            })
                          }
                        />
                      </div>
                      {activeDraft ? (
                        <div className={styles.toolbarEnd}>
                          <CharacterHashtagCountView
                            draft={activeDraft}
                            isExpanded
                          />
                          {activeDraft.service.supportsThread(
                            activeDraft.updateType,
                          ) ? (
                            <StartThreadButtonView
                              tooltip={`Add ${activeDraft.service.nameOfPost}`}
                              onClick={(event): void =>
                                requestChannelSettingsSignup(
                                  event.currentTarget,
                                )
                              }
                              disabled
                              iconOnly={false}
                              indicator={<LockIcon size="small" />}
                            />
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </ComposerToolbarFrame>
                }
              />
            </ComposerSectionsColumn>
            <PublicPreviewPanel
              id={PREVIEW_PANEL_ID}
              open={activeSidePanel === 'preview'}
              text={text}
              media={media}
              mediaAccess={mediaAccess}
              networks={networks}
              activeNetwork={activeNetwork}
            />
            {initialTemplateId ? (
              <PublicInitialTemplate
                templateId={initialTemplateId}
                onHandled={clearInitialTemplate}
                onUseTemplate={requestTemplateUse}
              />
            ) : null}
            {activeSidePanel === 'templates' ? (
              <PublicTemplatesSidePanel onUseTemplate={requestTemplateUse} />
            ) : null}
          </ComposerFrame.Body>
          <Dialog.Separator />
          <ComposerFrame.Footer className={styles.footer}>
            <div className={styles.draftActions}>
              <ConditionalTooltip content={draftDisabledReason}>
                <Button
                  variant="tertiary"
                  size="large"
                  disabled={accountActionDisabled}
                  onClick={saveAsDraft}
                  aria-keyshortcuts={COMPOSER_SHORTCUTS.SAVE_DRAFT}
                  aria-describedby={
                    draftDisabledReason ? draftDisabledReasonId : undefined
                  }
                >
                  Save Draft
                </Button>
              </ConditionalTooltip>
              {draftDisabledReason ? (
                <VisuallyHidden id={draftDisabledReasonId}>
                  {draftDisabledReason}
                </VisuallyHidden>
              ) : null}
            </div>
            <div className={styles.postingActions}>
              <PublicPostingTimeSelector
                value={scheduledAt}
                disabled={isMediaBusy}
                onChange={selectPostingTime}
              />
              <ConditionalTooltip content={postActionReason}>
                <Button
                  size="large"
                  disabled={postActionDisabled}
                  className={styles.postingAction}
                  aria-describedby={
                    postActionReason ? postActionReasonId : undefined
                  }
                  onClick={(): void => {
                    if (scheduledAt) openAccountGate('schedule')
                    else openShareNow()
                  }}
                >
                  {scheduledAt ? 'Schedule' : 'Share Now'}
                </Button>
              </ConditionalTooltip>
            </div>
            {postActionReason ? (
              <VisuallyHidden id={postActionReasonId}>
                {postActionReason}
              </VisuallyHidden>
            ) : null}
          </ComposerFrame.Footer>
        </ComposerFrame.Content>
      </Dialog>

      <PublicShareNowDialog
        open={isShareNowOpen}
        text={text}
        media={media}
        mediaAccess={mediaAccess}
        networks={networks}
        onOpenChange={setIsShareNowOpen}
        onCreateAccount={createAccountAfterShare}
      />

      {accountHandoffIntent ? (
        <PublicAccountGateDialog
          open
          action={accountHandoffIntent.action}
          onOpenChange={(open): void => {
            if (!open) closeAccountGate()
          }}
          onCreateAccount={(): void => continueToAccount('signup')}
          onLogIn={(): void => continueToAccount('login')}
        />
      ) : null}

      {featureSignupRequest ? (
        <PublicFeatureSignupDialog
          {...featureSignupRequest}
          onClose={(): void => setFeatureSignupRequest(undefined)}
          onContinueToAccount={continueFromSignupPrompt}
        />
      ) : null}

      {mediaToEdit ? (
        <PublicGuestImageWorkspace
          media={mediaToEdit.media}
          initialTask={mediaToEdit.task}
          onContinueToAccount={continueFromSignupPrompt}
          currentMediaIds={media.map((asset) => asset.id)}
          canTagPeople={canTagPeople}
          getReturnFocusTarget={(): HTMLElement | null =>
            imageEditTriggerRef.current ?? null
          }
          onReplace={(replacement, trackingSessionId): void => {
            setMediaOperationError(undefined)
            persistMedia(
              mediaRef.current.map((asset) =>
                asset.id === replacement.id ? replacement : asset,
              ),
              isCurrentPublicAction(trackingSessionId),
            )
          }}
          onClose={(): void => setMediaToEdit(undefined)}
        />
      ) : null}

      <AlertDialog
        open={isCloseConfirmationOpen}
        onOpenChange={setIsCloseConfirmationOpen}
      >
        <AlertDialog.Content>
          <AlertDialog.Title>Leave without saving?</AlertDialog.Title>
          <AlertDialog.Description>
            Your latest changes are not saved. If you leave, you will lose them.
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel>
              <Button variant="tertiary">Keep Editing</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button variant="critical" onClick={leaveComposer}>
                Leave
              </Button>
            </AlertDialog.Action>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>

      <AlertDialog
        open={pendingTemplate !== null}
        onOpenChange={(open): void => {
          if (!open) setPendingTemplate(null)
        }}
      >
        <AlertDialog.Content
          onCloseAutoFocus={(event): void => {
            event.preventDefault()
            if (templateTriggerRef.current?.isConnected) {
              templateTriggerRef.current.focus()
            } else {
              editor.tf.focus()
            }
          }}
        >
          <AlertDialog.Title>Replace your post text?</AlertDialog.Title>
          <AlertDialog.Description>
            This template will replace your current text.
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel>
              <Button variant="tertiary">Keep My Text</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                onClick={(): void => {
                  if (pendingTemplate) applyTemplate(pendingTemplate)
                }}
              >
                Replace Text
              </Button>
            </AlertDialog.Action>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>
    </>
  )
}
